import { useId, useMemo, useState } from 'react';
import { MapPin, Search, X, Loader2 } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface CommuneResult {
  code: string;
  nom: string;
  codePostal: string;
  codeDepartement: string;
  codeRegion: string;
  centre?: { coordinates: [number, number] };
}

interface CitySelectProps {
  value: CommuneResult | null;
  onChange: (commune: CommuneResult | null) => void;
  placeholder?: string;
  className?: string;
  /** id de l'input (pour `<label htmlFor>` côté parent) */
  inputId?: string;
}

function debounce<T extends (...args: Parameters<T>) => void>(fn: T, ms: number): T {
  let timer: ReturnType<typeof setTimeout>;
  return ((...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  }) as T;
}

export function CitySelect({
  value,
  onChange,
  placeholder = 'Votre commune (ex : Lyon, Bordeaux…)',
  className,
  inputId,
}: CitySelectProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CommuneResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const generatedId = useId();
  const listboxId = `${inputId ?? generatedId}-listbox`;

  // useMemo (au lieu de useCallback) parce que la fonction `debounce(...)` est
  // créée à l'extérieur du hook — on stabilise l'instance côté composant.
  const search = useMemo(
    () =>
      debounce(async (q: string) => {
        if (q.length < 2) {
          setResults([]);
          setLoading(false);
          return;
        }
        setLoading(true);
        try {
          const res = await fetch(`/api/geo/communes?q=${encodeURIComponent(q)}&limit=8`);
          if (!res.ok) throw new Error('geo api error');
          const data: CommuneResult[] = await res.json();
          setResults(data);
          setOpen(true);
        } catch {
          setResults([]);
        } finally {
          setLoading(false);
        }
      }, 200),
    [],
  );

  const handleInput = (q: string) => {
    setQuery(q);
    if (value) onChange(null);
    search(q);
  };

  const handleSelect = (commune: CommuneResult) => {
    onChange(commune);
    setQuery(`${commune.nom} (${commune.codePostal})`);
    setOpen(false);
    setResults([]);
  };

  const handleClear = () => {
    onChange(null);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div className={cn('relative', className)}>
      {/* Input — wrappé dans un parent role=combobox pour supporter aria-expanded
          (un input[type=text] a un role=textbox implicite, et aria-expanded
          n'est pas valide sur textbox — cf. eslint jsx-a11y/role-supports-aria-props).
          On expose toujours aria-controls (le listbox) avec un id généré si
          inputId n'est pas fourni, pour satisfaire jsx-a11y/role-has-required-aria-props. */}
      <div
        className="relative"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listboxId}
      >
        <MapPin
          className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400"
          strokeWidth={1.5}
        />
        <input
          id={inputId}
          type="text"
          value={query}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={placeholder}
          aria-label="Rechercher une commune"
          aria-autocomplete="list"
          aria-controls={listboxId}
          className={cn(
            'h-11 w-full rounded-xl border border-stone-200/70 bg-paper pl-10 pr-10 text-sm text-ink shadow-subtle transition-all duration-150 placeholder:text-stone-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20',
            value && 'border-brand-300 bg-brand-50/30',
          )}
        />
        {loading ? (
          <Loader2 className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-stone-400" />
        ) : query || value ? (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Effacer la commune"
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-0.5 text-stone-400 transition-colors hover:text-ink"
          >
            <X className="h-4 w-4" />
          </button>
        ) : (
          <Search
            className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-300"
            strokeWidth={1.5}
          />
        )}
      </div>

      {/* Dropdown */}
      {open && results.length > 0 && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Communes suggérées"
          className="absolute left-0 right-0 top-full z-50 mt-1.5 overflow-hidden rounded-xl border border-stone-200/70 bg-paper shadow-floating"
        >
          {results.map((commune) => (
            <li key={commune.code}>
              <button
                type="button"
                role="option"
                aria-selected={value?.code === commune.code}
                onClick={() => handleSelect(commune)}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-stone-50"
              >
                <MapPin className="h-3.5 w-3.5 shrink-0 text-stone-400" strokeWidth={1.5} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-ink">{commune.nom}</span>
                  <span className="block text-[11px] text-stone-500">
                    {commune.codePostal} · Dép. {commune.codeDepartement}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {(!open || results.length === 0) && (
        <ul id={listboxId} role="listbox" aria-label="Communes suggérées" hidden />
      )}
    </div>
  );
}
